/**
 * FeedbackNugget — Inline contextual feedback prompt placed at strategic workflow spots.
 *
 * States: dormant → expanded → submitted
 * Each placement has its own feature flag and localStorage key to prevent re-showing.
 * Session cap limits total nugget impressions to 2 per session.
 */
import { useState } from 'react';
import { useFeatureFlagSafe } from '@/contexts/FeatureFlagContext';
import { useFeedbackWidget } from '@/contexts/FeedbackContext';
import { useSubmitFeedback } from '@/hooks/useFeedback';
import { canShowNugget, recordNuggetImpression } from '@/utils/feedbackSessionCap';
import type { FeedbackSubmission } from '@/types/feedback';

const RATINGS = [
  { id: 'great', emoji: '👍', label: 'Great' },
  { id: 'ok', emoji: '👌', label: 'Fine' },
  { id: 'confusing', emoji: '😕', label: 'Confusing' },
  { id: 'broken', emoji: '🐛', label: 'Broken' },
] as const;

type RatingId = (typeof RATINGS)[number]['id'];

/** Map nugget rating to the submission category for the API */
function ratingToCategory(rating: RatingId): FeedbackSubmission['category'] {
  switch (rating) {
    case 'great':
    case 'ok':
      return 'love';
    case 'confusing':
      return 'confusing';
    case 'broken':
      return 'bug';
  }
}

interface FeedbackNuggetProps {
  /** Human-readable feature name (shown in expanded header) */
  feature: string;
  /** Source column value, e.g. "nugget_upload" */
  featureKey: string;
  /** Feature flag key, e.g. "beta_feedback_upload_enabled" */
  flagKey: string;
  /** Prompt text displayed in dormant and expanded states */
  question: string;
}

export function FeedbackNugget({ feature, featureKey, flagKey, question }: FeedbackNuggetProps) {
  const flagEnabled = useFeatureFlagSafe(flagKey);
  const { openWidget } = useFeedbackWidget();
  const submitFeedback = useSubmitFeedback();

  const [state, setState] = useState<'dormant' | 'expanded' | 'submitted'>('dormant');
  const [rating, setRating] = useState<RatingId | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Determine visibility once on mount: check localStorage and session cap.
  // Lazy initializer avoids setState-in-effect and only runs once.
  const [visible] = useState(() => {
    if (!flagEnabled) return false;
    if (
      typeof window !== 'undefined' &&
      localStorage.getItem(`feedback_nugget_${featureKey}`) === 'submitted'
    )
      return false;
    if (!canShowNugget()) return false;
    recordNuggetImpression();
    return true;
  });

  if (!visible) return null;

  const handleSubmit = () => {
    if (!rating) return;

    const payload: FeedbackSubmission = {
      submission_type: 'contextual',
      category: ratingToCategory(rating),
      source: featureKey,
      rating,
      message: comment || `Rated: ${rating}`,
      page_url: window.location.pathname,
    };

    submitFeedback.mutate(payload, {
      onSuccess: () => {
        localStorage.setItem(`feedback_nugget_${featureKey}`, 'submitted');
        setState('submitted');
      },
      onError: () => {
        setError('Failed to submit. Please try again.');
      },
    });
  };

  // ── SUBMITTED STATE ──────────────────────────────────────
  if (state === 'submitted') {
    return (
      <div className="my-2.5 px-3.5 py-2 bg-success-bg border border-success-border rounded-md flex items-center gap-2">
        <span className="font-sans text-[11px] font-semibold text-success">
          ✓ Thanks for the feedback on {feature}.
        </span>
      </div>
    );
  }

  // ── DORMANT STATE (invitation) ───────────────────────────
  if (state === 'dormant') {
    return (
      <button
        onClick={() => setState('expanded')}
        className="my-2.5 px-3.5 py-2 bg-warning-bg border border-dashed border-sand rounded-md cursor-pointer flex items-center gap-2 w-full text-left hover:border-sand-border transition-colors"
      >
        <span className="font-sans text-[9px] font-bold tracking-[1.5px] uppercase text-gold bg-[rgba(184,134,11,0.1)] px-1.5 py-0.5 rounded-[3px] flex-shrink-0">
          BETA
        </span>
        <span className="font-serif text-[12px] text-espresso flex-1">{question}</span>
        <span className="font-sans text-[11px] text-gold-text flex-shrink-0">→</span>
      </button>
    );
  }

  // ── EXPANDED STATE (rate + comment) ──────────────────────
  return (
    <div className="my-2.5 bg-white border border-sand rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={`px-3.5 py-2.5 flex justify-between items-start ${rating ? 'border-b border-parchment' : ''}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="font-sans text-[9px] font-bold tracking-[1.5px] uppercase text-gold">
              FEEDBACK
            </span>
            <span className="font-sans text-[9px] text-warm-gray-light">·</span>
            <span className="font-sans text-[9px] font-semibold tracking-[1px] uppercase text-warm-gray">
              {feature}
            </span>
          </div>
          <div className="font-serif text-[13px] text-ink leading-[1.4]">{question}</div>
        </div>
        <button
          onClick={() => {
            setState('dormant');
            setRating(null);
            setComment('');
          }}
          className="bg-transparent border-none text-warm-gray-light text-[16px] cursor-pointer leading-none pl-2"
          aria-label="Close feedback"
        >
          ×
        </button>
      </div>

      {/* Quick rating */}
      <div className="px-3.5 py-2.5 flex gap-2" role="radiogroup" aria-label="Rate this feature">
        {RATINGS.map((r) => (
          <button
            key={r.id}
            onClick={() => {
              setRating(r.id);
              setError(null);
            }}
            className={`flex-1 py-2.5 px-1.5 rounded-md cursor-pointer text-center transition-all border ${
              rating === r.id
                ? 'bg-ink border-ink'
                : 'bg-cream-light border-sand hover:border-sand-border'
            }`}
            role="radio"
            aria-checked={rating === r.id}
            aria-label={r.label}
          >
            <div className="text-[20px] mb-0.5">{r.emoji}</div>
            <div
              className={`font-sans text-[9px] font-semibold ${rating === r.id ? 'text-linen' : 'text-warm-gray'}`}
            >
              {r.label}
            </div>
          </button>
        ))}
      </div>

      {/* Comment + submit (appears after rating) */}
      {rating && (
        <div className="px-3.5 pb-3">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Any specifics? (optional)"
            rows={2}
            className="w-full px-2.5 py-2 border border-sand rounded font-serif text-[12px] text-ink bg-cream-light resize-none leading-[1.5] mb-2 focus:outline-none focus:border-sand-border"
          />
          {error && (
            <div className="px-3.5 py-1.5 text-[11px] font-serif text-red-600" role="alert">
              {error}
            </div>
          )}
          <div className="flex justify-between items-center">
            <button
              onClick={handleSubmit}
              disabled={submitFeedback.isPending}
              className="bg-ink border-none font-sans text-[11px] font-bold text-linen px-4 py-2 rounded cursor-pointer tracking-[0.5px] hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitFeedback.isPending ? 'Sending...' : 'Submit'}
            </button>
            <button
              onClick={() => openWidget(featureKey)}
              className="bg-transparent border-none font-sans text-[10px] text-gold-text cursor-pointer underline underline-offset-2"
            >
              More to share? →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
